import { React, useEffect } from 'react'
import styled from 'styled-components'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Button } from 'react-bootstrap'
import PropTypes from 'prop-types'
import SVGIcon from 'reusableComponents/SVGIcon'
import { ReactComponent as PenToSquare } from 'assets/svg/pen-to-square-fa.svg'
import { ReactComponent as Pencil } from 'assets/svg/pencil.svg'
import { colors } from 'globalStyles'
import {
  setIsGuestMode,
  setIsVerifiedUser,
  setAllowRenewSession,
} from 'store/shipping/shippingSlice'
import { verifyRecipientSession } from 'api/shipping'
import { selectIsAuthenticated } from 'store/shipping/shippingSelectors'
import { setCookieToLocalStorage } from 'utils/helperFunctions'
import PackageDeliveryPreferencesView from './PackageDeliveryPreferencesView'
import {
  penToSquareIconStyle,
  pencilIconStyle,
  buttonStyle,
  styledBaseDiv,
  styledSubDiv,
} from './PackageDeliveryPreferences.style'

const StyledBaseDiv = styled.div`
  display: flex;
  flex-direction: row !important;
  position: relative;
  padding: 8px 4px;
  border-radius: 8px 0px;
  min-height: 52px;
  width: 100%;
  border-radius: 8px 0px;
  display: block;
`

const PackageDeliveryPreferences = ({ packageDeliveryDetails }) => {
  const { t, i18n } = useTranslation()
  const { lang } = useParams()
  const { language } = i18n
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const isAuthenticated = useSelector(selectIsAuthenticated)

  const getDeliverySignature = () => {
    let signaturePreference = t(`my_preferences.none_selected`)
    if (packageDeliveryDetails.adultSignatureRequired) {
      signaturePreference = t(`my_preferences.adult_signature_required`)
    } else if (!packageDeliveryDetails.signatureRequired) {
      signaturePreference = t(`my_preferences.sign_not_required`)
    } else if (
      packageDeliveryDetails.signaturePreference === 'Regular' ||
      packageDeliveryDetails.chainOfSignature ||
      packageDeliveryDetails.expressChequeFlag ||
      packageDeliveryDetails.hasSignatureException ||
      (packageDeliveryDetails.dangerousGoods && packageDeliveryDetails.dgType === 'Exceptional')
    ) {
      signaturePreference = t(`deliveryPreferencesSummary.no_sign_waive`)
    } else if (packageDeliveryDetails.signaturePreference === 'PTL') {
      signaturePreference = t(`deliveryPreferencesSummary.sign_waived`)
    } else if (packageDeliveryDetails.signatureRequired) {
      signaturePreference = t(`my_preferences.sign_required`)
    }

    return signaturePreference
  }

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang)
    }
  }, [lang, i18n, language])

  const onClickChangeDeliveryOptions = async () => {
    if (isAuthenticated) {
      try {
        const dataToByPassOtpVerification = await verifyRecipientSession(
          packageDeliveryDetails.shipmentid,
          lang
        )
        const verifyData = dataToByPassOtpVerification.data.sessionData
        if (verifyData.response === 'SESSION_CREATED') {
          const { sessionUUID, expiryTime, updateExpTime } = verifyData
          setCookieToLocalStorage(packageDeliveryDetails.pinhash, sessionUUID, expiryTime)
          dispatch(setIsVerifiedUser(true))
          dispatch(setAllowRenewSession(updateExpTime))
          dispatch(setIsGuestMode(true))
          navigate(`/rs/tr/${lang}/${packageDeliveryDetails.pinhash}`)
        } else {
          dispatch(setIsVerifiedUser(false))
          dispatch(setIsGuestMode(false))
        }
      } catch (error) {
        console.error('Verification', 'verifiy recipient session failed due to: ', error)
      }
    }
  }

  return (
    <StyledBaseDiv>
      <PackageDeliveryPreferencesView
        icon={PenToSquare}
        label={t(`my_preferences.signature_preferences`)}
        value={getDeliverySignature()}
        iconStyle={penToSquareIconStyle}
      />
      <div style={styledBaseDiv}>
        <div style={styledSubDiv}>
          <SVGIcon
            Icon={Pencil}
            iconStyle={pencilIconStyle}
            color={colors.primary_blue}
            aria-label="Edit delivery options"
          />
        </div>
        <Button style={buttonStyle} variant="link" onClick={onClickChangeDeliveryOptions}>
          {t(`my_preferences.change_del_options`)}
        </Button>
      </div>
    </StyledBaseDiv>
  )
}

PackageDeliveryPreferences.propTypes = {
  packageDeliveryDetails: PropTypes.shape({
    signaturePreference: PropTypes.string.isRequired,
    adultSignatureRequired: PropTypes.bool.isRequired,
    signatureRequired: PropTypes.bool.isRequired,
    chainOfSignature: PropTypes.bool.isRequired,
    hasSignatureException: PropTypes.bool.isRequired,
    expressChequeFlag: PropTypes.bool.isRequired,
    dangerousGoods: PropTypes.bool.isRequired,
    dgType: PropTypes.string.isRequired,
    locationPreference: PropTypes.string.isRequired,
    shipmentid: PropTypes.string.isRequired,
    pinhash: PropTypes.string.isRequired,
  }).isRequired,
}

export default PackageDeliveryPreferences
